import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import Style from '../../../../constants/Style';
import Colors from '../../../../constants/Colors';
import TabButton from '../../../../components/TabButton';
import { screenMaxWidth } from '../../../../components/TopSection';
import { AssetInstance, Template, UserPermission } from '../../../../constants/Types';
import LinkIcon from '../../../../components/24pxIcons/LinkIcon';
import Overview from './components/Overview';
import NotesSection from './components/NotesSection';
import TagSection from './components/TagSection';
import getAssetTickets from '../../../../requests/Assets/GetAssetTickets';
import EditAssetView from './components/EditAssetView';
import Button from '../../../../components/Button';
import EditIcon from '../../../../components/EditIcon';
import LeftArrow16 from '../../../../components/16pxIcons/LeftArrow16';
import PressableStyled from '../../../../components/PressableStyled';
import { logEvent } from '../../../../components/LogEvent';
import { DELETE_ASSET_SUCCESS, PPM_URL_MISSING } from '../../../../constants/AlertMessages';
import ReportIssueIcon from '../../../../components/12pxIcons/ReportIssueIcon';
import ReportTicketModal from '../../../../components/AssetManagement/ReportTicketModal';
import retireAsset from '../../../../requests/Assets/RetireAsset';
import BinIcon12 from '../../../../components/12pxIcons/BinIcon12';
import { openInNewTab } from '../../../../constants/Utils';
import isMobile from '../../../../components/IsMobile';
import BackArrow from '../../../../components/BackIcon';
import ReportTicketIcon24 from '../../../../components/24pxIcons/ReportTicketIcon24';
import TabSelectionMobile from '../../../../components/TabSelectionMobile';
import EditAssetModal from '../../../../components/AssetManagement/EditAssetModal';
import { loadCustomFields } from '../../../../components/AssetManagement/data';
import PPMSection from './components/PPMSection';
import TicketsLog from './components/TicketsLog';

// eslint-disable-next-line no-unused-vars, no-shadow
enum DetailsTab { OVERVIEW = 'Overview', TAG = 'Tag', ACTIVITY = 'Activity', PPM = 'PPM' }

interface SelectedAssetProps {
  asset: AssetInstance,
  template: Template,
  showScreenAlert,
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setSelectedAsset: Dispatch<SetStateAction<AssetInstance>>,
  deleteAsset,
  updateSelectedAsset,
}

const SelectedAsset = ({
  asset, template, showScreenAlert, loading, setLoading, setSelectedAsset, deleteAsset,
  updateSelectedAsset,
}: SelectedAssetProps) => {
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditAsset: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_UPDATE);
  const canDeleteAsset: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_DELETE);
  const canReportTicket: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_CREATE);
  const buildingId: string = asset?.buildingId ?? selectedBuildingId;
  const [selectedTab, setSelectedTab] = useState<DetailsTab>(DetailsTab.OVERVIEW);
  const [ticketCount, setTicketCount] = useState<number | undefined>(undefined);
  const [domain, setDomain] = useState<string>('https://hereworks.tech/');
  const [editViewShown, setEditViewShown] = useState<boolean>(false);
  const [reportTicketModalShown, setReportTicketModalShown] = useState<boolean>(false);
  const [editAssetModalShown, setEditAssetModalShown] = useState<boolean>(false);

  if (!asset) return null;

  const refresh: () => void = () => {
    getAssetTickets(asset.id, buildingId).then((response) => {
      if (!response?.Tickets) return;
      setTicketCount(response.Tickets.all ?? response.Tickets.year);
    });
  };

  useEffect(() => {
    AsyncStorage.getItem('environment').then((result) => {
      if (result === 'dev') setDomain('https://dev.hereworks.tech/');
      else if (result === 'demo') setDomain('https://demo.hereworks.tech/');
    });
    refresh();
  }, []);

  const addTag = (tagId: string) => updateSelectedAsset({
    ...asset,
    solveTagIds: asset.solveTagIds ? [...asset.solveTagIds, tagId] : [tagId],
  });

  const deleteError = (errorText?: string) => showScreenAlert(
    'Error',
    errorText ?? 'Unable to delete the asset at the moment. Please try again later',
  );

  const onPressDeleteAsset: () => void = () => {
    logEvent('AssetMgmtScreenMobile', 'delete_asset_icon');
    showScreenAlert(
      'Delete the asset?',
      <Text>
        {' Do you want to delete the asset - '}
        <Text style={Style.alertBoldText}>{asset.name}</Text>
        {'?'}
      </Text>,
      true,
      () => {
        logEvent('AssetMgmtScreenMobile', 'delete_asset_yes');
        setLoading(true);
        retireAsset(asset.id).then((response) => {
          if (response && !response.isError) {
            showScreenAlert('Success', DELETE_ASSET_SUCCESS, false);
            deleteAsset(asset.id);
          } else deleteError(response?.errorText);
          setLoading(false);
        }).catch((error) => {
          deleteError(error?.errorText);
          setLoading(false);
        });
      },
      'Delete',
    );
  };

  const showReportTicketModal: () => void = () => {
    if (!template) {
      showScreenAlert('Error', 'The template used to create this asset has been deleted');
      return;
    }
    setReportTicketModalShown(true);
  };

  return <>
    {editViewShown ? <EditAssetView
      {...{
        asset,
        template,
        showScreenAlert,
        loading,
        setLoading,
        setSelectedAsset,
        setEditViewShown,
        onPressDeleteAsset,
        updateSelectedAsset,
      }}
    /> : <>
      <View style={[styles.header, { maxWidth: screenMaxWidth }]}>
        <PressableStyled
          style={styles.backButton}
          onPress={() => setSelectedAsset(null)}
        >
          {isMobile ? <BackArrow /> : <LeftArrow16 />}
        </PressableStyled>
        <Text style={styles.assetName}>
          {asset?.name}
        </Text>
        <View style={Style.row}>
          {isMobile ? (canReportTicket && <PressableStyled onPress={showReportTicketModal}>
            {<ReportTicketIcon24 />}
          </PressableStyled>) : <>
            {canReportTicket && <Button
              children={<ReportIssueIcon style={styles.buttonIcon} />}
              onPress={showReportTicketModal}
              style={styles.button}
              title='Report an issue'
            />}
            {canEditAsset && <Button
              affirmative
              title='Edit asset'
              onPress={() => setEditViewShown(true)}
              children={<EditIcon
                style={styles.buttonIcon}
                color={'#FFF'}
              />}
              style={styles.button}
            />}
          </>}
        </View>
      </View>
      {isMobile ? <View style={{ flex: 1 }}>
        <View style={{ flex: 1 }}>
          <TabSelectionMobile
            options={Object.values(DetailsTab).filter((tab: DetailsTab) => tab !== DetailsTab.PPM)}
            selectOption={setSelectedTab}
            selectedOption={selectedTab}
            titles={Object.values(DetailsTab).map((tab: DetailsTab) => tab.toUpperCase())}
          />
          {selectedTab === DetailsTab.OVERVIEW
            && <Overview {...{ asset, template, showScreenAlert }} />}
          {selectedTab === DetailsTab.TAG && <TagSection
            {...{
              asset, showScreenAlert, setLoading, domain, addTag,
            }}
          />}
          {selectedTab === DetailsTab.ACTIVITY && <View style={styles.content}>
            <TicketsLog {...{ asset, ticketCount }} />
            <NotesSection {...{ asset, setSelectedAsset, showScreenAlert }} />
          </View>}
          {selectedTab === DetailsTab.PPM && <PPMSection />}
        </View>
        {canEditAsset && <Button
          title='Edit asset'
          affirmative
          onPress={() => setEditAssetModalShown(true)}
          children={<EditIcon
            style={styles.buttonIcon}
            color={'#FFF'}
          />}
          style={styles.editButtonMobile}
        />}
      </View> : <View style={[styles.container, { maxWidth: screenMaxWidth }]}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>{selectedTab}</Text>
            <View style={Style.row}>
              {canDeleteAsset && <Button
                children={<BinIcon12 color={styles.deleteAssetButton.borderColor} />}
                onPress={onPressDeleteAsset}
                style={styles.deleteAssetButton}
              />}
              {Object.values(DetailsTab).map((tab: DetailsTab) => tab !== DetailsTab.ACTIVITY
                && <TabButton
                  title={tab.toUpperCase()}
                  key={tab}
                  select={() => {
                    if (tab === DetailsTab.PPM) {
                      logEvent('AssetRow', 'ppm_button');
                      const ppmLink = `${template?.ppmUrl}${asset.id}`;
                      if (!template?.ppmUrl) showScreenAlert('Error', PPM_URL_MISSING);
                      else openInNewTab(ppmLink);
                      return;
                    }
                    setSelectedTab(tab);
                  }}
                  selected={selectedTab === tab}
                  children={tab === DetailsTab.PPM && <LinkIcon />}
                />)}
            </View>
          </View>
          {selectedTab === DetailsTab.OVERVIEW
            && <Overview {...{ asset, template, showScreenAlert }} />}
          {selectedTab === DetailsTab.TAG && <TagSection
            {...{
              asset, showScreenAlert, setLoading, domain, addTag,
            }}
          />}
        </View>
        <View style={[styles.content, styles.lastContentSection]}>
          <Text style={styles.title}>{'Activity'}</Text>
          <TicketsLog {...{ asset, ticketCount }} />
          <NotesSection {...{ asset, setSelectedAsset, showScreenAlert }} />
        </View>
      </View>}
      <ReportTicketModal
        isVisible={reportTicketModalShown}
        hide={() => setReportTicketModalShown(false)}
        assetName={asset.name}
        solveTagId={asset.id}
        {...{ template, refresh }}
      />
      {isMobile && <EditAssetModal
        visible={editAssetModalShown}
        setModalVisible={setEditAssetModalShown}
        {...{ asset, deleteAsset }}
        updateAsset={updateSelectedAsset}
        image={asset.imageUrl?.length > 0 ? `${asset.imageUrl}?time=${Date.now()}` : null}
        updateImage={() => { }}
        customFields={loadCustomFields(template, asset, false)}
        templateType={template?.type}
      />}
    </>}
    <ActivityIndicator
      size='large'
      style={Style.activityIndicator}
      animating={loading}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flex: 1,
  },
  content: {
    flex: 1,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: '#FFF',
    borderRadius: 16,
    padding: 16,
    marginEnd: isMobile ? undefined : 24,
    marginTop: isMobile ? 16 : undefined,
  },
  lastContentSection: {
    marginEnd: 0,
  },
  header: {
    ...Style.row,
    paddingBottom: 16,
    marginEnd: isMobile ? undefined : 8,
  },
  title: {
    fontSize: 20,
    lineHeight: 32,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  button: {
    height: 40,
    paddingVertical: 8,
    paddingHorizontal: 24,
    flexDirection: 'row-reverse',
  },
  buttonIcon: {
    marginEnd: 8,
  },
  backButton: {
    borderRadius: 100,
    borderWidth: isMobile ? undefined : 1,
    backgroundColor: isMobile ? undefined : '#FFF',
    borderColor: Colors.light.hereworksBlue100,
    width: isMobile ? 24 : 36,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginEnd: isMobile ? 8 : 24,
  },
  assetName: {
    flex: 1,
    fontSize: isMobile ? 22 : 32,
    lineHeight: isMobile ? 33 : 48,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 16,
  },
  deleteAssetButton: {
    aspectRatio: 1,
    height: 32,
    paddingHorizontal: 0,
    borderColor: Colors.light.hereworksRed500,
    marginEnd: 8,
  },
  editButtonMobile: {
    flexDirection: 'row-reverse',
    flex: undefined,
    width: 135,
    height: 40,
    marginTop: 8,
    marginBottom: Platform.OS === 'android' ? 16 : undefined,
  },
});

export default SelectedAsset;
